import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { createFloorTexture } from './textures.js';
import { createToonGradientMap, createSharedWorldMaterials, toonMat, foliageToonMat, windUniform, type SharedWorldMaterials } from './materials.js';
import {
  buildBiomeAccentCluster,
  buildCrane,
  buildDepotSilo,
  buildDockLight,
  buildFlagBanner,
  buildLandmarkSign,
  buildNeonBillboard,
  buildNeonAccentStrip,
  buildPathStrip,
  buildRadioMast,
  buildRouteSightlineScreen,
  buildSignalDish,
  buildWaterTower,
  markAmbientPulse,
  markAmbientSpin,
  markDecorative,
} from './FpsPoiLandmarks.js';

export type FootstepSurface = 'grass' | 'wood' | 'metal';

export function tagFootstepSurface(mesh: THREE.Object3D, surface: FootstepSurface): void {
  mesh.userData.footstepSurface = surface;
}

export interface BuiltWorld {
  syncables: { mesh: THREE.Object3D; body: CANNON.Body }[];
  sceneRoot: THREE.Group;
  waterMaterial: THREE.MeshStandardMaterial | null;
  decoGroup?: THREE.Group;
  getTerrainHeight: (x: number, z: number) => number;
  destructibleMeshes: THREE.Mesh[];
}

/** POI cluster centers — used to flatten terrain around key buildings */
const POI_CENTERS: [number, number][] = [
  [2, -37],     // Tilted Towers
  [-35, 22],    // Retail Ridge
  [38, 16],     // Pleasant Point
  [-22, -28],   // Lucky Wharf
  [28, -22],    // Cracked Cove
  [32, -32],    // Dusty Depot
  [-30, 30],    // Snobby Shores
  [-18, -12],   // Shifty Shafts
];

function generateTerrainMesh(
  grassMaterial: THREE.MeshToonMaterial,
): { mesh: THREE.Mesh; getHeight: (worldX: number, worldZ: number) => number } {
  const SEGS = 128;
  const SIZE = 340;
  const HALF = SIZE / 2;
  const geometry = new THREE.PlaneGeometry(SIZE, SIZE, SEGS, SEGS);
  const pos = geometry.attributes.position;

  for (let i = 0; i < pos.count; i++) {
    const px = pos.getX(i); // -170 to 170
    const py = pos.getY(i); // -170 to 170 (before rotation, Y is the second in-plane axis)
    // px = world X, py = world Z (after -PI/2 X rotation)
    const x = px;
    const z = py;

    // Multi-octave hills + ridges for readable BR-scale terrain
    let h =
      6.8 * Math.sin(x * 0.008 + 0.7) * Math.cos(z * 0.0065 + 1.2) +
      3.1 * Math.sin(x * 0.022 + 2.1) * Math.sin(z * 0.018 + 0.5) +
      1.35 * Math.sin(x * 0.041 + z * 0.033) * Math.cos(z * 0.029) +
      0.55 * Math.sin(x * 0.09) * Math.cos(z * 0.095) +
      0.22 * Math.sin(x * 0.21 + z * 0.17);

    // Gentle ridge along NE–SW diagonal for map landmarks
    const ridge = Math.exp(-Math.pow((x + z) * 0.012, 2)) * 2.4;
    h += ridge;

    // Flatten center 40m for final circle combat
    const distFromCenter = Math.sqrt(x * x + z * z);
    const flattenFactor = Math.min(distFromCenter / 42, 1);
    h *= flattenFactor;

    // Flatten within 28m of each POI center (keep POI floors readable)
    for (const [px2, pz2] of POI_CENTERS) {
      const d = Math.sqrt((x - px2) * (x - px2) + (z - pz2) * (z - pz2));
      if (d < 28) {
        const blend = Math.max(0, 1 - d / 28);
        h = h * (1 - blend);
      }
    }

    pos.setZ(i, h);
  }
  // Phase 31X-11: height-based grass tint (low = darker, high = lighter/drier)
  const colors = new Float32Array(pos.count * 3);
  let hMin = Infinity;
  let hMax = -Infinity;
  for (let i = 0; i < pos.count; i++) {
    const hz = pos.getZ(i);
    if (hz < hMin) hMin = hz;
    if (hz > hMax) hMax = hz;
  }
  const hRange = Math.max(0.001, hMax - hMin);
  for (let i = 0; i < pos.count; i++) {
    const t = (pos.getZ(i) - hMin) / hRange;
    // low valleys: deep green; ridges: warmer olive
    colors[i * 3] = 0.22 + t * 0.28;
    colors[i * 3 + 1] = 0.42 + t * 0.22;
    colors[i * 3 + 2] = 0.2 + t * 0.08;
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.computeVertexNormals();

  grassMaterial.vertexColors = true;
  const mesh = new THREE.Mesh(geometry, grassMaterial);
  mesh.rotation.x = -Math.PI / 2;
  mesh.receiveShadow = true;

  // Build a lookup grid for fast bilinear height sampling
  // After -PI/2 X rotation, pos.getX(i) = worldX, pos.getY(i) = worldZ, pos.getZ(i) = height
  const heightData = new Float32Array(pos.count);
  for (let i = 0; i < pos.count; i++) {
    heightData[i] = pos.getZ(i);
  }
  const VERTS = SEGS + 1; // vertices per side

  function getHeight(worldX: number, worldZ: number): number {
    // Map world coords to [0, SEGS]
    const u = ((worldX + HALF) / SIZE) * SEGS;
    const v = ((worldZ + HALF) / SIZE) * SEGS;
    const ui = Math.floor(Math.max(0, Math.min(SEGS - 1, u)));
    const vi = Math.floor(Math.max(0, Math.min(SEGS - 1, v)));
    const uf = u - ui;
    const vf = v - vi;
    // PlaneGeometry vertex order: row-major, Y increases upward in local space
    // Index for (col, row) = row * VERTS + col
    const i00 = vi * VERTS + ui;
    const i10 = vi * VERTS + (ui + 1);
    const i01 = (vi + 1) * VERTS + ui;
    const i11 = (vi + 1) * VERTS + (ui + 1);
    const h00 = heightData[i00] ?? 0;
    const h10 = heightData[i10] ?? 0;
    const h01 = heightData[i01] ?? 0;
    const h11 = heightData[i11] ?? 0;
    return h00 * (1 - uf) * (1 - vf) + h10 * uf * (1 - vf) + h01 * (1 - uf) * vf + h11 * uf * vf;
  }

  return { mesh, getHeight };
}

function addStylizedTree(
  root: THREE.Group,
  x: number,
  z: number,
  scale: number,
  grad: THREE.DataTexture,
  hueOverride?: number,
  yOffset = 0,
): THREE.Mesh {
  const g = new THREE.Group();
  const trunk = new THREE.Mesh(
    new THREE.CylinderGeometry(0.14 * scale, 0.2 * scale, 2.1 * scale, 9),
    toonMat(0x6d4c41, grad, { emissive: 0x221108, emissiveIntensity: 0.15 }),
  );
  trunk.position.y = 1.05 * scale;
  trunk.castShadow = true;
  trunk.receiveShadow = true;
  trunk.userData['destructible'] = true;
  trunk.userData['isTree'] = true;
  trunk.userData['maxHp'] = 80;
  trunk.userData['currentHp'] = 80;
  const hue = hueOverride ?? 0.28 + Math.sin(x * 0.1 + z * 0.07) * 0.06;
  const col = new THREE.Color().setHSL(hue, 0.62, 0.46);
  const foliage = new THREE.Mesh(
    new THREE.ConeGeometry(1.35 * scale, 3.1 * scale, 11),
    foliageToonMat(col.getHex(), grad, {
      emissive: col.clone().multiplyScalar(0.4).getHex(),
      emissiveIntensity: 0.28,
    }),
  );
  foliage.position.y = 2.75 * scale;
  foliage.castShadow = true;
  foliage.receiveShadow = true;
  g.add(trunk, foliage);
  g.position.set(x, yOffset, z);
  g.rotation.y = (x + z) * 0.15;
  root.add(g);
  return trunk;
}

function addSnowyTree(root: THREE.Group, x: number, z: number, scale: number, grad: THREE.DataTexture, yOffset = 0): THREE.Mesh {
  const g = new THREE.Group();
  const trunk = new THREE.Mesh(
    new THREE.CylinderGeometry(0.13 * scale, 0.2 * scale, 1.6 * scale, 9),
    toonMat(0x5a3a26, grad, { emissive: 0x1a0e06, emissiveIntensity: 0.1 }),
  );
  trunk.position.y = 0.8 * scale;
  trunk.castShadow = true;
  trunk.userData['destructible'] = true;
  trunk.userData['isTree'] = true;
  trunk.userData['maxHp'] = 80;
  trunk.userData['currentHp'] = 80;
  g.add(trunk);
  for (let i = 0; i < 3; i++) {
    const t = i / 2;
    const cone = new THREE.Mesh(
      new THREE.ConeGeometry((1.5 - t * 0.45) * scale, 1.2 * scale, 10),
      foliageToonMat(0xa6dff7, grad, { emissive: 0xffffff, emissiveIntensity: 0.32 }),
    );
    cone.position.y = (1.6 + i * 0.85) * scale;
    cone.castShadow = true;
    g.add(cone);
  }
  g.position.set(x, yOffset, z);
  g.rotation.y = Math.random() * Math.PI * 2;
  root.add(g);
  return trunk;
}

function addPalmTree(root: THREE.Group, x: number, z: number, scale: number, grad: THREE.DataTexture, yOffset = 0): THREE.Mesh {
  const g = new THREE.Group();
  const trunk = new THREE.Mesh(
    new THREE.CylinderGeometry(0.13 * scale, 0.18 * scale, 3.4 * scale, 8),
    toonMat(0x8c6a3c, grad, { emissive: 0x2a1a08, emissiveIntensity: 0.08 }),
  );
  trunk.position.y = 1.7 * scale;
  trunk.rotation.z = (Math.random() - 0.5) * 0.18;
  trunk.castShadow = true;
  trunk.userData['destructible'] = true;
  trunk.userData['isTree'] = true;
  trunk.userData['maxHp'] = 80;
  trunk.userData['currentHp'] = 80;
  g.add(trunk);
  for (let i = 0; i < 7; i++) {
    const frond = new THREE.Mesh(
      new THREE.ConeGeometry(0.18 * scale, 1.5 * scale, 5),
      foliageToonMat(0x4cce6a, grad, { emissive: 0x123012, emissiveIntensity: 0.22 }),
    );
    const a = (i / 7) * Math.PI * 2;
    frond.position.set(Math.cos(a) * 0.5 * scale, 3.4 * scale, Math.sin(a) * 0.5 * scale);
    frond.lookAt(Math.cos(a) * 4 * scale, 1 * scale, Math.sin(a) * 4 * scale);
    frond.castShadow = true;
    g.add(frond);
  }
  g.position.set(x, yOffset, z);
  root.add(g);
  return trunk;
}

function addRock(
  root: THREE.Group,
  x: number,
  z: number,
  scale: number,
  grad: THREE.DataTexture,
  color = 0x9aa3ad,
  matOrYOffset?: THREE.MeshToonMaterial | number,
  yOffset = 0,
): void {
  const resolvedYOffset = typeof matOrYOffset === 'number' ? matOrYOffset : yOffset;
  const rockMat = typeof matOrYOffset === 'object'
    ? matOrYOffset
    : toonMat(color, grad, { emissive: new THREE.Color(color).multiplyScalar(0.1).getHex(), emissiveIntensity: 0.1 });
  const geo = new THREE.IcosahedronGeometry(scale, 1);
  // Distort for stylized rock look
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const f = 0.85 + Math.random() * 0.3;
    pos.setXYZ(i, pos.getX(i) * f, pos.getY(i) * f, pos.getZ(i) * f);
  }
  geo.computeVertexNormals();
  const rock = new THREE.Mesh(geo, rockMat);
  rock.position.set(x, scale * 0.5 + resolvedYOffset, z);
  rock.castShadow = true;
  rock.receiveShadow = true;
  markDecorative(rock, 115);
  root.add(rock);
}

function addDecorativeCoverCluster(
  root: THREE.Group,
  x: number,
  z: number,
  yOffset: number,
  shared: SharedWorldMaterials,
  palette?: { crate: number; tarp: number; barrel: number },
): void {
  const g = new THREE.Group();
  g.position.set(x, yOffset, z);
  g.rotation.y = (x * 0.17 + z * 0.11) % (Math.PI * 2);

  const crateMat = palette
    ? toonMat(palette.crate, shared.grad, {
        emissive: new THREE.Color(palette.crate).multiplyScalar(0.16).getHex(),
        emissiveIntensity: 0.1,
      })
    : shared.coverCrate;
  const tarpMat = palette
    ? toonMat(palette.tarp, shared.grad, {
        emissive: new THREE.Color(palette.tarp).multiplyScalar(0.22).getHex(),
        emissiveIntensity: 0.12,
      })
    : shared.coverTarp;
  const barrelMat = palette
    ? toonMat(palette.barrel, shared.grad, {
        emissive: new THREE.Color(palette.barrel).multiplyScalar(0.18).getHex(),
        emissiveIntensity: 0.1,
      })
    : shared.coverBarrel;

  const crateA = new THREE.Mesh(new THREE.BoxGeometry(1.15, 0.95, 1.05), crateMat);
  crateA.position.set(-0.7, 0.48, 0.05);
  crateA.castShadow = true;
  crateA.receiveShadow = true;
  g.add(crateA);

  const crateB = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.7, 0.9), crateMat);
  crateB.position.set(0.35, 0.35, -0.55);
  crateB.rotation.y = 0.35;
  crateB.castShadow = true;
  crateB.receiveShadow = true;
  g.add(crateB);

  const tarp = new THREE.Mesh(new THREE.BoxGeometry(2.25, 0.12, 1.25), tarpMat);
  tarp.position.set(-0.18, 1.08, -0.08);
  tarp.rotation.z = -0.08;
  tarp.castShadow = true;
  g.add(tarp);

  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.3, 0.82, 12), barrelMat);
  barrel.position.set(1.05, 0.42, 0.48);
  barrel.rotation.z = 0.08;
  barrel.castShadow = true;
  barrel.receiveShadow = true;
  g.add(barrel);

  const barrier = new THREE.Mesh(new THREE.BoxGeometry(2.05, 0.42, 0.24), crateMat);
  barrier.position.set(0.1, 0.22, 0.92);
  barrier.rotation.y = -0.18;
  barrier.castShadow = true;
  barrier.receiveShadow = true;
  g.add(barrier);

  const shrub = new THREE.Mesh(
    new THREE.SphereGeometry(0.48, 9, 7),
    shared.foliageGreen,
  );
  shrub.position.set(-1.38, 0.28, 0.68);
  shrub.scale.set(1.2, 0.58, 0.85);
  shrub.castShadow = true;
  shrub.receiveShadow = true;
  g.add(shrub);

  addRock(g, 1.45, -0.3, 0.32, shared.grad, 0x8d949b, shared.rockGray);
  addRock(g, -1.32, -0.72, 0.24, shared.grad, 0xa4abb2, shared.rockGray);

  markDecorative(g, 115);
  root.add(g);
}

function addPathSideMarker(
  root: THREE.Group,
  x: number,
  z: number,
  yOffset: number,
  rotY: number,
  shared: SharedWorldMaterials,
  color: number,
): void {
  const g = new THREE.Group();
  g.position.set(x, yOffset, z);
  g.rotation.y = rotY;

  const postMat = shared.woodPost;
  const markerMat = toonMat(color, shared.grad, {
    emissive: new THREE.Color(color).multiplyScalar(0.28).getHex(),
    emissiveIntensity: 0.18,
  });

  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.06, 1.35, 7), postMat);
  post.position.y = 0.68;
  post.castShadow = true;
  g.add(post);

  const board = new THREE.Mesh(new THREE.BoxGeometry(0.58, 0.34, 0.06), markerMat);
  board.position.set(0, 1.24, 0.04);
  board.castShadow = true;
  g.add(board);

  const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.055, 0.07), postMat);
  stripe.position.set(0, 1.24, 0.08);
  g.add(stripe);

  markDecorative(g, 105);
  root.add(g);
}

function addWildflowerPatch(
  root: THREE.Group,
  cx: number,
  cz: number,
  grad: THREE.DataTexture,
  count = 14,
): void {
  const g = new THREE.Group();
  g.position.set(cx, 0, cz);
  const petalColors = [0xffee55, 0xff88cc, 0xcc77ff, 0xffffff, 0xffaa44];
  const stemMat = toonMat(0x3a7a32, grad, { emissive: 0x143814, emissiveIntensity: 0.14 });
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + Math.random() * 0.5;
    const r = 0.35 + Math.random() * 1.6;
    const fx = Math.cos(a) * r;
    const fz = Math.sin(a) * r;
    const stemH = 0.12 + Math.random() * 0.1;
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.02, stemH, 5), stemMat);
    stem.position.set(fx, stemH * 0.5 + 0.02, fz);
    g.add(stem);
    const petal = new THREE.Mesh(
      new THREE.SphereGeometry(0.06 + Math.random() * 0.04, 6, 5),
      foliageToonMat(petalColors[i % petalColors.length]!, grad, {
        emissive: petalColors[i % petalColors.length]!,
        emissiveIntensity: 0.28,
      }),
    );
    petal.position.set(fx, stemH + 0.04, fz);
    petal.scale.y = 0.55;
    g.add(petal);
  }
  markDecorative(g, 85);
  root.add(g);
}

/** Decorative meteor impact — shallow depression with dark scorch ring. */
function addMeteorCrater(root: THREE.Group, cx: number, cz: number, grad: THREE.DataTexture): void {
  const g = new THREE.Group();
  const bowl = new THREE.Mesh(
    new THREE.CylinderGeometry(2.4, 3.4, 0.32, 18),
    toonMat(0x3a3228, grad, { emissive: 0x1a1208, emissiveIntensity: 0.16 }),
  );
  bowl.position.y = -0.1;
  bowl.receiveShadow = true;
  g.add(bowl);

  const ring = new THREE.Mesh(
    new THREE.RingGeometry(3.1, 5.4, 24),
    toonMat(0x1a1410, grad, { emissive: 0x0a0806, emissiveIntensity: 0.22 }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.03;
  ring.receiveShadow = true;
  g.add(ring);

  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + Math.random() * 0.4;
    const r = 3.6 + Math.random() * 1.2;
    addRock(g, Math.cos(a) * r, Math.sin(a) * r, 0.22 + Math.random() * 0.18, grad, 0x5a5048);
  }

  g.position.set(cx, 0, cz);
  markDecorative(g, 120);
  root.add(g);
}

/** Builds a small house with walls, roof, door opening, and physics colliders. */
function addBuilding(
  root: THREE.Group,
  world: CANNON.World,
  floorMaterial: CANNON.Material,
  cx: number,
  cz: number,
  opts: {
    w: number;
    d: number;
    h: number;
    wallColor: number;
    roofColor: number;
    grad: THREE.DataTexture;
    doorSide?: 'N' | 'S' | 'E' | 'W';
  },
  buildingId: string,
): THREE.Mesh[] {
  const { w, d, h, wallColor, roofColor, grad, doorSide = 'S' } = opts;
  const t = 0.3;
  const doorW = Math.min(1.4, w * 0.32);
  const doorH = Math.min(2.2, h * 0.8);

  const wallMat = toonMat(wallColor, grad, {
    emissive: new THREE.Color(wallColor).multiplyScalar(0.15).getHex(),
    emissiveIntensity: 0.12,
  });
  const trimMat = toonMat(0x3a2a18, grad);
  const windowMat = new THREE.MeshStandardMaterial({
    color: 0xaadfff,
    emissive: 0x8fd0ff,
    emissiveIntensity: 0.55,
    roughness: 0.25,
    metalness: 0.2,
  });

  const destructibles: THREE.Mesh[] = [];

  const tagDestructible = (mesh: THREE.Mesh, maxHp = 350): void => {
    mesh.userData['destructible'] = true;
    mesh.userData['maxHp'] = maxHp;
    mesh.userData['currentHp'] = maxHp;
    mesh.userData['buildingId'] = buildingId;
    destructibles.push(mesh);
  };

  const makeWall = (
    sx: number,
    sy: number,
    sz: number,
    px: number,
    py: number,
    pz: number,
    isDoorSide = false,
  ) => {
    if (!isDoorSide) {
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, sz), wallMat);
      mesh.position.set(px, py, pz);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      tagDestructible(mesh);
      root.add(mesh);
      const body = new CANNON.Body({ mass: 0, material: floorMaterial });
      body.addShape(new CANNON.Box(new CANNON.Vec3(sx / 2, sy / 2, sz / 2)));
      body.position.set(px, py, pz);
      world.addBody(body);
      return;
    }
    // Wall with door opening (split into 3 panels: left, right, lintel above)
    const isXAligned = sx > sz;
    if (isXAligned) {
      const sideW = (sx - doorW) / 2;
      const leftX = px - sx / 2 + sideW / 2;
      const rightX = px + sx / 2 - sideW / 2;
      const lintelH = sy - doorH;
      // Left panel
      const m1 = new THREE.Mesh(new THREE.BoxGeometry(sideW, sy, sz), wallMat);
      m1.position.set(leftX, py, pz);
      m1.castShadow = true;
      m1.receiveShadow = true;
      tagDestructible(m1);
      root.add(m1);
      const b1 = new CANNON.Body({ mass: 0, material: floorMaterial });
      b1.addShape(new CANNON.Box(new CANNON.Vec3(sideW / 2, sy / 2, sz / 2)));
      b1.position.set(leftX, py, pz);
      world.addBody(b1);
      // Right panel
      const m2 = new THREE.Mesh(new THREE.BoxGeometry(sideW, sy, sz), wallMat);
      m2.position.set(rightX, py, pz);
      m2.castShadow = true;
      m2.receiveShadow = true;
      tagDestructible(m2);
      root.add(m2);
      const b2 = new CANNON.Body({ mass: 0, material: floorMaterial });
      b2.addShape(new CANNON.Box(new CANNON.Vec3(sideW / 2, sy / 2, sz / 2)));
      b2.position.set(rightX, py, pz);
      world.addBody(b2);
      // Lintel
      if (lintelH > 0.05) {
        const ly = py + sy / 2 - lintelH / 2;
        const m3 = new THREE.Mesh(new THREE.BoxGeometry(doorW, lintelH, sz), wallMat);
        m3.position.set(px, ly, pz);
        m3.castShadow = true;
        m3.receiveShadow = true;
        tagDestructible(m3);
        root.add(m3);
        const b3 = new CANNON.Body({ mass: 0, material: floorMaterial });
        b3.addShape(new CANNON.Box(new CANNON.Vec3(doorW / 2, lintelH / 2, sz / 2)));
        b3.position.set(px, ly, pz);
        world.addBody(b3);
      }
      // Door trim
      const trimT = 0.08;
      const trimMesh = new THREE.Mesh(new THREE.BoxGeometry(doorW + 0.1, trimT, sz + 0.04), trimMat);
      trimMesh.position.set(px, py + sy / 2 - lintelH - trimT / 2, pz);
      root.add(trimMesh);
    } else {
      const sideD = (sz - doorW) / 2;
      const leftZ = pz - sz / 2 + sideD / 2;
      const rightZ = pz + sz / 2 - sideD / 2;
      const lintelH = sy - doorH;
      const m1 = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, sideD), wallMat);
      m1.position.set(px, py, leftZ);
      m1.castShadow = true;
      m1.receiveShadow = true;
      tagDestructible(m1);
      root.add(m1);
      const b1 = new CANNON.Body({ mass: 0, material: floorMaterial });
      b1.addShape(new CANNON.Box(new CANNON.Vec3(sx / 2, sy / 2, sideD / 2)));
      b1.position.set(px, py, leftZ);
      world.addBody(b1);
      const m2 = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, sideD), wallMat);
      m2.position.set(px, py, rightZ);
      m2.castShadow = true;
      m2.receiveShadow = true;
      tagDestructible(m2);
      root.add(m2);
      const b2 = new CANNON.Body({ mass: 0, material: floorMaterial });
      b2.addShape(new CANNON.Box(new CANNON.Vec3(sx / 2, sy / 2, sideD / 2)));
      b2.position.set(px, py, rightZ);
      world.addBody(b2);
      if (lintelH > 0.05) {
        const ly = py + sy / 2 - lintelH / 2;
        const m3 = new THREE.Mesh(new THREE.BoxGeometry(sx, lintelH, doorW), wallMat);
        m3.position.set(px, ly, pz);
        m3.castShadow = true;
        m3.receiveShadow = true;
        tagDestructible(m3);
        root.add(m3);
        const b3 = new CANNON.Body({ mass: 0, material: floorMaterial });
        b3.addShape(new CANNON.Box(new CANNON.Vec3(sx / 2, lintelH / 2, doorW / 2)));
        b3.position.set(px, ly, pz);
        world.addBody(b3);
      }
    }
  };

  // 4 walls
  const yMid = h / 2;
  makeWall(w, h, t, cx, yMid, cz - d / 2 + t / 2, doorSide === 'N');
  makeWall(w, h, t, cx, yMid, cz + d / 2 - t / 2, doorSide === 'S');
  makeWall(t, h, d, cx - w / 2 + t / 2, yMid, cz, doorSide === 'W');
  makeWall(t, h, d, cx + w / 2 - t / 2, yMid, cz, doorSide === 'E');

  // Roof (pyramid)
  const roofGeo = new THREE.ConeGeometry(Math.hypot(w, d) * 0.62, h * 0.55, 4);
  const roof = new THREE.Mesh(
    roofGeo,
    toonMat(roofColor, grad, {
      emissive: new THREE.Color(roofColor).multiplyScalar(0.2).getHex(),
      emissiveIntensity: 0.18,
    }),
  );
  roof.position.set(cx, h + h * 0.27, cz);
  roof.rotation.y = Math.PI / 4;
  roof.castShadow = true;
  tagDestructible(roof, 350);
  root.add(roof);
  // Roof collider (simple top box)
  const roofBody = new CANNON.Body({ mass: 0, material: floorMaterial });
  roofBody.addShape(new CANNON.Box(new CANNON.Vec3(w / 2, 0.2, d / 2)));
  roofBody.position.set(cx, h + 0.2, cz);
  world.addBody(roofBody);

  // Add some windows on non-door walls
  const winSize = 0.7;
  const winY = h * 0.6;
  const addWindow = (px: number, py: number, pz: number, rotY: number) => {
    const wm = new THREE.Mesh(new THREE.PlaneGeometry(winSize, winSize), windowMat);
    wm.position.set(px, py, pz);
    wm.rotation.y = rotY;
    root.add(wm);
  };
  if (doorSide !== 'N') {
    addWindow(cx - w * 0.25, winY, cz - d / 2 - 0.02, 0);
    addWindow(cx + w * 0.25, winY, cz - d / 2 - 0.02, 0);
  }
  if (doorSide !== 'S') {
    addWindow(cx - w * 0.25, winY, cz + d / 2 + 0.02, Math.PI);
    addWindow(cx + w * 0.25, winY, cz + d / 2 + 0.02, Math.PI);
  }

  // Interior floor (mat for visual; outside ground already exists)
  const floorMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(w - t * 2, d - t * 2),
    toonMat(0x4d3a26, grad),
  );
  floorMesh.rotation.x = -Math.PI / 2;
  floorMesh.position.set(cx, 0.02, cz);
  floorMesh.receiveShadow = true;
  tagFootstepSurface(floorMesh, 'wood');
  root.add(floorMesh);

  return destructibles;
}

/** Tall landmark billboard — visible from distance at major POIs. */
function addPoiLandmarkBillboard(
  root: THREE.Group,
  x: number,
  z: number,
  label: string,
  color: number,
  grad: THREE.DataTexture,
): void {
  const g = new THREE.Group();
  g.position.set(x, 0, z);
  g.rotation.y = Math.atan2(-x, -z);
  root.add(g);

  const frameMat = toonMat(color, grad, {
    emissive: new THREE.Color(color).multiplyScalar(0.35).getHex(),
    emissiveIntensity: 0.42,
  });
  const postMat = toonMat(0x3a3028, grad, { emissive: 0x120c08, emissiveIntensity: 0.1 });

  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.14, 7.2, 8), postMat);
  post.position.y = 3.6;
  post.castShadow = true;
  g.add(post);

  const cv = document.createElement('canvas');
  cv.width = 320;
  cv.height = 128;
  const ctx = cv.getContext('2d')!;
  const hex = `#${color.toString(16).padStart(6, '0')}`;
  const gradFill = ctx.createLinearGradient(0, 0, 0, cv.height);
  gradFill.addColorStop(0, hex);
  gradFill.addColorStop(1, '#1a1420');
  ctx.fillStyle = gradFill;
  ctx.fillRect(0, 0, cv.width, cv.height);
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 6;
  ctx.strokeRect(4, 4, cv.width - 8, cv.height - 8);
  ctx.fillStyle = 'rgba(0,0,0,0.22)';
  ctx.fillRect(0, cv.height - 18, cv.width, 18);
  ctx.font = 'bold 34px system-ui,sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#fff';
  ctx.strokeStyle = '#000';
  ctx.lineWidth = 5;
  ctx.strokeText(label, cv.width / 2, cv.height / 2 - 4);
  ctx.fillText(label, cv.width / 2, cv.height / 2 - 4);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;

  const boardW = 4.8;
  const boardH = 1.92;
  const boardY = 6.2;
  const board = new THREE.Mesh(
    new THREE.PlaneGeometry(boardW, boardH),
    new THREE.MeshStandardMaterial({
      map: tex,
      emissive: 0xffffff,
      emissiveMap: tex,
      emissiveIntensity: 0.48,
      side: THREE.DoubleSide,
    }),
  );
  board.position.y = boardY;
  board.castShadow = true;
  g.add(board);

  const frameT = 0.12;
  const topFrame = new THREE.Mesh(new THREE.BoxGeometry(boardW + 0.24, frameT, 0.14), frameMat);
  topFrame.position.set(0, boardY + boardH / 2 + frameT / 2, 0);
  g.add(topFrame);
  const botFrame = new THREE.Mesh(new THREE.BoxGeometry(boardW + 0.24, frameT, 0.14), frameMat);
  botFrame.position.set(0, boardY - boardH / 2 - frameT / 2, 0);
  g.add(botFrame);

  const accent = new THREE.PointLight(color, 1.8, 14);
  accent.position.set(0, boardY, 0.6);
  g.add(accent);
}

/** Banner/sign for a POI. */
function addPoiSign(root: THREE.Group, x: number, z: number, label: string, color: number, grad: THREE.DataTexture): void {
  const post = new THREE.Mesh(
    new THREE.CylinderGeometry(0.08, 0.08, 4, 8),
    toonMat(0x553322, grad),
  );
  post.position.set(x, 2, z);
  post.castShadow = true;
  root.add(post);

  // Stylized text via 2D canvas texture
  const cv = document.createElement('canvas');
  cv.width = 256;
  cv.height = 72;
  const ctx = cv.getContext('2d')!;
  ctx.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
  ctx.fillRect(0, 0, cv.width, cv.height);
  ctx.fillStyle = 'rgba(0,0,0,0.18)';
  ctx.fillRect(0, cv.height - 12, cv.width, 12);
  ctx.font = 'bold 38px system-ui,sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#fff';
  ctx.strokeStyle = '#000';
  ctx.lineWidth = 4;
  ctx.strokeText(label, cv.width / 2, cv.height / 2);
  ctx.fillText(label, cv.width / 2, cv.height / 2);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  const board = new THREE.Mesh(
    new THREE.PlaneGeometry(3.6, 1),
    new THREE.MeshStandardMaterial({
      map: tex,
      emissive: 0xffffff,
      emissiveMap: tex,
      emissiveIntensity: 0.35,
      side: THREE.DoubleSide,
    }),
  );
  board.position.set(x, 3.6, z);
  board.castShadow = true;
  board.rotation.y = Math.atan2(-x, -z);
  root.add(board);
}

/** Static cover crate with physics collider. */
function addCoverCrate(
  root: THREE.Group,
  world: CANNON.World,
  floorMaterial: CANNON.Material,
  x: number,
  z: number,
  opts: { w: number; h: number; d: number; color: number; grad: THREE.DataTexture; rotY?: number },
): void {
  const { w, h, d, color, grad, rotY = 0 } = opts;
  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(w, h, d),
    toonMat(color, grad, {
      emissive: new THREE.Color(color).multiplyScalar(0.18).getHex(),
      emissiveIntensity: 0.14,
    }),
  );
  mesh.position.set(x, h / 2 + 0.02, z);
  mesh.rotation.y = rotY;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  root.add(mesh);
  const body = new CANNON.Body({ mass: 0, material: floorMaterial });
  body.addShape(new CANNON.Box(new CANNON.Vec3(w / 2, h / 2, d / 2)));
  body.position.set(x, h / 2 + 0.02, z);
  body.quaternion.setFromEuler(0, rotY, 0, 'XYZ');
  world.addBody(body);
}

/** Waist-high half-wall for mid-fight cover. */
function addHalfWall(
  root: THREE.Group,
  world: CANNON.World,
  floorMaterial: CANNON.Material,
  x: number,
  z: number,
  rotY: number,
  opts: { w: number; h: number; t: number; color: number; grad: THREE.DataTexture },
): void {
  const { w, h, t, color, grad } = opts;
  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(w, h, t),
    toonMat(color, grad, {
      emissive: new THREE.Color(color).multiplyScalar(0.12).getHex(),
      emissiveIntensity: 0.1,
    }),
  );
  mesh.position.set(x, h / 2 + 0.02, z);
  mesh.rotation.y = rotY;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  root.add(mesh);
  const body = new CANNON.Body({ mass: 0, material: floorMaterial });
  body.addShape(new CANNON.Box(new CANNON.Vec3(w / 2, h / 2, t / 2)));
  body.position.set(x, h / 2 + 0.02, z);
  body.quaternion.setFromEuler(0, rotY, 0, 'XYZ');
  world.addBody(body);
}

export const REBOOT_VAN_X = 0;
export const REBOOT_VAN_Z = 22;
export const VEND_X = 18;
export const VEND_Z = 16;
export const CRAFT_BENCH_X = 38;
export const CRAFT_BENCH_Z = 10;
export const SLURP_BARREL_X = 28;
export const SLURP_BARREL_Z = -22;
export const SHIELD_KEG_X = 5;
export const SHIELD_KEG_Z = -40;

/** Simple wooden quest-sign standee — decorative prop; interactive NPC mesh lives in FpsNpcQuest. */
function addNpcStandee(root: THREE.Group, x: number, z: number, grad: THREE.DataTexture): void {
  const g = new THREE.Group();
  g.position.set(x, 0, z);
  g.rotation.y = Math.PI * 0.12;
  root.add(g);

  const wood = toonMat(0x7a5a32, grad, { emissive: 0x281808, emissiveIntensity: 0.12 });
  const post = new THREE.Mesh(new THREE.BoxGeometry(0.14, 1.05, 0.14), wood);
  post.position.y = 0.52;
  post.castShadow = true;
  g.add(post);

  const board = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.52, 0.06), wood);
  board.position.set(0, 1.12, 0.04);
  board.castShadow = true;
  g.add(board);

  const accent = toonMat(0xffd966, grad, { emissive: 0x665010, emissiveIntensity: 0.45 });
  const plaque = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.28, 0.04), accent);
  plaque.position.set(0, 1.12, 0.1);
  g.add(plaque);
}

/** Wooden crafting table with green glow ring at Pleasant Point. */
function addCraftBench(
  root: THREE.Group,
  x: number,
  z: number,
  grad: THREE.DataTexture,
): void {
  const g = new THREE.Group();
  g.position.set(x, 0, z);
  g.rotation.y = Math.PI * 0.18;
  root.add(g);

  const woodMat = toonMat(0x8b6914, grad, {
    emissive: 0x332208,
    emissiveIntensity: 0.14,
  });
  const woodDark = toonMat(0x5c4510, grad, {
    emissive: 0x221806,
    emissiveIntensity: 0.1,
  });
  const glowMat = toonMat(0x44ff88, grad, {
    emissive: 0x22cc66,
    emissiveIntensity: 0.72,
  });

  const top = new THREE.Mesh(new THREE.BoxGeometry(2.25, 0.14, 1.15), woodMat);
  top.position.y = 0.96;
  top.castShadow = true;
  top.receiveShadow = true;
  tagFootstepSurface(top, 'wood');
  g.add(top);

  const apron = new THREE.Mesh(new THREE.BoxGeometry(2.05, 0.08, 0.95), woodDark);
  apron.position.set(0, 0.86, 0);
  g.add(apron);

  const legGeo = new THREE.BoxGeometry(0.14, 0.88, 0.14);
  for (const [lx, lz] of [
    [-0.92, -0.42],
    [0.92, -0.42],
    [-0.92, 0.42],
    [0.92, 0.42],
  ] as const) {
    const leg = new THREE.Mesh(legGeo, woodDark);
    leg.position.set(lx, 0.44, lz);
    leg.castShadow = true;
    g.add(leg);
  }

  const anvil = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.18, 0.28), woodDark);
  anvil.position.set(0.55, 1.08, 0.1);
  g.add(anvil);

  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.62, 0.07, 10, 24), glowMat);
  ring.rotation.x = Math.PI / 2;
  ring.position.y = 0.1;
  markAmbientPulse(ring, { material: glowMat, base: 0.62, amount: 0.16, speed: 1.25 });
  markAmbientSpin(ring, 0.16);
  g.add(ring);

  const beacon = new THREE.PointLight(0x44ff88, 2.2, 10);
  beacon.position.set(0, 1.25, 0);
  markAmbientPulse(beacon, { light: beacon, base: 1.8, amount: 0.28, speed: 1.25 });
  g.add(beacon);
  markDecorative(g, 125);
}

/** Glowing slurp barrel — E interact grants shield once per match (handled in FPSGame). */
function addSlurpBarrel(
  root: THREE.Group,
  x: number,
  z: number,
  grad: THREE.DataTexture,
): void {
  const g = new THREE.Group();
  g.position.set(x, 0, z);
  root.add(g);

  const barrelMat = toonMat(0x44e8c8, grad, {
    emissive: 0x22ccaa,
    emissiveIntensity: 0.55,
  });
  const rimMat = toonMat(0x2a9a88, grad, {
    emissive: 0x118866,
    emissiveIntensity: 0.35,
  });
  const glowMat = new THREE.MeshStandardMaterial({
    color: 0x66ffee,
    emissive: 0x44ffcc,
    emissiveIntensity: 1.35,
    roughness: 0.2,
    metalness: 0.15,
    transparent: true,
    opacity: 0.82,
  });

  const h = 1.05;
  const r = 0.38;
  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(r, r, h, 16), barrelMat);
  barrel.position.y = h / 2 + 0.02;
  barrel.castShadow = true;
  barrel.receiveShadow = true;
  g.add(barrel);

  const rim = new THREE.Mesh(new THREE.TorusGeometry(r + 0.04, 0.05, 8, 20), rimMat);
  rim.rotation.x = Math.PI / 2;
  rim.position.y = h + 0.02;
  g.add(rim);

  const drip = new THREE.Mesh(new THREE.SphereGeometry(0.14, 10, 8), glowMat);
  drip.position.set(0, 0.12, 0);
  drip.userData.slurpGlow = { mat: glowMat, base: 1.35 };
  g.add(drip);

  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.55, 0.06, 10, 24), glowMat.clone());
  const ringGlow = ring.material as THREE.MeshStandardMaterial;
  ringGlow.transparent = true;
  ringGlow.opacity = 0.55;
  ring.userData.slurpGlow = { mat: ringGlow, base: 1.1 };
  ring.rotation.x = Math.PI / 2;
  ring.position.y = 0.08;
  markAmbientPulse(ring, { material: ringGlow, base: 1.05, amount: 0.18, speed: 1.55, phase: 0.4 });
  markAmbientSpin(ring, 0.2);
  g.add(ring);

  const beacon = new THREE.PointLight(0x44ffcc, 1.8, 8);
  beacon.position.set(0, 0.85, 0);
  markAmbientPulse(beacon, { light: beacon, base: 1.45, amount: 0.26, speed: 1.55 });
  g.add(beacon);
  markDecorative(g, 110);
}

/** Blue shield keg — one-time +40 shield interactable at Tilted. */
function addShieldKeg(
  root: THREE.Group,
  x: number,
  z: number,
  grad: THREE.DataTexture,
): void {
  const g = new THREE.Group();
  g.position.set(x, 0, z);
  root.add(g);

  const barrelMat = toonMat(0x4499ff, grad, {
    emissive: 0x2266cc,
    emissiveIntensity: 0.55,
  });
  const rimMat = toonMat(0x2a6aaa, grad, {
    emissive: 0x114488,
    emissiveIntensity: 0.35,
  });
  const glowMat = new THREE.MeshStandardMaterial({
    color: 0x66bbff,
    emissive: 0x4488ff,
    emissiveIntensity: 1.25,
    roughness: 0.2,
    metalness: 0.15,
    transparent: true,
    opacity: 0.82,
  });

  const h = 1.05;
  const r = 0.38;
  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(r, r, h, 16), barrelMat);
  barrel.position.y = h / 2 + 0.02;
  barrel.castShadow = true;
  g.add(barrel);

  const drip = new THREE.Mesh(new THREE.SphereGeometry(0.14, 10, 8), glowMat);
  drip.position.set(0, 0.12, 0);
  g.add(drip);

  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.55, 0.06, 10, 24), glowMat.clone());
  ring.rotation.x = Math.PI / 2;
  ring.position.y = 0.08;
  markAmbientPulse(ring, { material: ring.material, base: 1.0, amount: 0.16, speed: 1.4, phase: 0.8 });
  markAmbientSpin(ring, 0.18);
  g.add(ring);

  const beacon = new THREE.PointLight(0x4488ff, 1.6, 7);
  beacon.position.set(0, 0.85, 0);
  markAmbientPulse(beacon, { light: beacon, base: 1.32, amount: 0.22, speed: 1.4 });
  g.add(beacon);
  markDecorative(g, 110);
}

/** Purple vending machine with glowing Q sign and static box collider. */
function addVendingMachine(
  root: THREE.Group,
  world: CANNON.World,
  floorMaterial: CANNON.Material,
  x: number,
  z: number,
  grad: THREE.DataTexture,
): void {
  const g = new THREE.Group();
  g.position.set(x, 0, z);
  g.rotation.y = -Math.PI * 0.22;
  root.add(g);

  const bodyPurple = 0x6b2fd4;
  const bodyDark = 0x3a1578;
  const bodyMat = toonMat(bodyPurple, grad, {
    emissive: 0x2a0a58,
    emissiveIntensity: 0.28,
  });
  const trimMat = toonMat(bodyDark, grad, { emissive: 0x180838, emissiveIntensity: 0.18 });
  const glowMat = new THREE.MeshStandardMaterial({
    color: 0xcc88ff,
    emissive: 0xaa55ff,
    emissiveIntensity: 1.45,
    roughness: 0.22,
    metalness: 0.18,
  });

  const cabinet = new THREE.Mesh(new THREE.BoxGeometry(2.1, 3.35, 1.55), bodyMat);
  cabinet.position.y = 1.72;
  cabinet.castShadow = true;
  cabinet.receiveShadow = true;
  g.add(cabinet);

  const canopy = new THREE.Mesh(new THREE.BoxGeometry(2.35, 0.28, 1.75), trimMat);
  canopy.position.y = 3.48;
  g.add(canopy);

  const window = new THREE.Mesh(
    new THREE.BoxGeometry(1.55, 1.85, 0.08),
    new THREE.MeshStandardMaterial({
      color: 0xd8b8ff,
      emissive: 0x8844cc,
      emissiveIntensity: 0.55,
      roughness: 0.18,
      metalness: 0.2,
      transparent: true,
      opacity: 0.78,
    }),
  );
  window.position.set(0, 2.05, 0.78);
  g.add(window);

  const shelf = new THREE.Mesh(new THREE.BoxGeometry(1.45, 0.08, 1.2), trimMat);
  shelf.position.set(0, 1.35, 0.35);
  g.add(shelf);

  const signBoard = new THREE.Mesh(new THREE.BoxGeometry(1.05, 1.05, 0.1), trimMat);
  signBoard.position.set(0, 2.95, 0.88);
  g.add(signBoard);

  const qRing = new THREE.Mesh(new THREE.TorusGeometry(0.28, 0.07, 10, 22), glowMat);
  qRing.position.set(0, 2.95, 0.94);
  markAmbientPulse(qRing, { material: glowMat, base: 1.28, amount: 0.22, speed: 1.05 });
  markAmbientSpin(qRing, 0.12);
  g.add(qRing);

  const qTail = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.22, 0.08), glowMat);
  qTail.position.set(0.18, 2.78, 0.94);
  qTail.rotation.z = -0.55;
  g.add(qTail);

  const pad = new THREE.Mesh(
    new THREE.CylinderGeometry(2.4, 2.65, 0.18, 18),
    toonMat(0x8844cc, grad, { emissive: 0x5522aa, emissiveIntensity: 0.48 }),
  );
  pad.position.set(0, 0.1, 0);
  pad.receiveShadow = true;
  g.add(pad);

  const beacon = new THREE.PointLight(0xbb66ff, 2.6, 12);
  beacon.position.set(0, 3.55, 0.55);
  markAmbientPulse(beacon, { light: beacon, base: 2.1, amount: 0.3, speed: 1.05 });
  g.add(beacon);

  const collW = 2.35;
  const collH = 3.55;
  const collD = 1.65;
  const body = new CANNON.Body({ mass: 0, material: floorMaterial });
  body.addShape(new CANNON.Box(new CANNON.Vec3(collW / 2, collH / 2, collD / 2)));
  body.position.set(x, collH / 2 + 0.02, z);
  body.quaternion.setFromEuler(0, g.rotation.y, 0, 'XYZ');
  world.addBody(body);
  markDecorative(g, 130);
}

/** Blue reboot van with glowing sign and static box collider. */
function addRebootVan(
  root: THREE.Group,
  world: CANNON.World,
  floorMaterial: CANNON.Material,
  x: number,
  z: number,
  grad: THREE.DataTexture,
): void {
  const g = new THREE.Group();
  g.position.set(x, 0, z);
  g.rotation.y = Math.PI * 0.12;
  root.add(g);

  const vanBlue = 0x2a6fd4;
  const vanDark = 0x1a4588;
  const bodyMat = toonMat(vanBlue, grad, {
    emissive: 0x0a2858,
    emissiveIntensity: 0.18,
  });
  const trimMat = toonMat(vanDark, grad, { emissive: 0x081838, emissiveIntensity: 0.12 });
  const glowMat = new THREE.MeshStandardMaterial({
    color: 0x66ccff,
    emissive: 0x44aaff,
    emissiveIntensity: 1.35,
    roughness: 0.25,
    metalness: 0.15,
  });

  const chassis = new THREE.Mesh(new THREE.BoxGeometry(4.2, 1.05, 2.05), bodyMat);
  chassis.position.y = 0.72;
  chassis.castShadow = true;
  chassis.receiveShadow = true;
  g.add(chassis);

  const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.85, 1.35, 1.95), bodyMat);
  cabin.position.set(-1.05, 1.42, 0);
  cabin.castShadow = true;
  g.add(cabin);

  const windshield = new THREE.Mesh(
    new THREE.BoxGeometry(0.08, 0.85, 1.55),
    new THREE.MeshStandardMaterial({
      color: 0xaadfff,
      emissive: 0x6ab8ff,
      emissiveIntensity: 0.45,
      roughness: 0.2,
      metalness: 0.25,
      transparent: true,
      opacity: 0.82,
    }),
  );
  windshield.position.set(-1.92, 1.48, 0);
  g.add(windshield);

  const bumper = new THREE.Mesh(new THREE.BoxGeometry(4.35, 0.18, 2.15), trimMat);
  bumper.position.y = 0.28;
  g.add(bumper);

  const wheelMat = toonMat(0x1a1a22, grad);
  for (const [wx, wz] of [
    [-1.35, 0.95],
    [1.35, 0.95],
    [-1.35, -0.95],
    [1.35, -0.95],
  ] as const) {
    const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.38, 0.38, 0.28, 14), wheelMat);
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(wx, 0.38, wz);
    wheel.castShadow = true;
    g.add(wheel);
  }

  const signPost = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.08, 2.4, 8), trimMat);
  signPost.position.set(0.55, 2.35, 1.18);
  g.add(signPost);

  const signBoard = new THREE.Mesh(new THREE.BoxGeometry(1.35, 1.35, 0.12), trimMat);
  signBoard.position.set(0.55, 3.35, 1.18);
  g.add(signBoard);

  const rebootRing = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.09, 10, 24), glowMat);
  rebootRing.position.set(0.55, 3.35, 1.25);
  rebootRing.rotation.y = Math.PI * 0.12;
  markAmbientPulse(rebootRing, { material: glowMat, base: 1.18, amount: 0.2, speed: 1.15 });
  markAmbientSpin(rebootRing, 0.1);
  g.add(rebootRing);

  const rebootArrow = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.32, 8), glowMat);
  rebootArrow.position.set(0.55, 3.62, 1.25);
  rebootArrow.rotation.z = Math.PI;
  g.add(rebootArrow);

  const pad = new THREE.Mesh(
    new THREE.CylinderGeometry(4.6, 5, 0.22, 20),
    toonMat(0x3388ff, grad, { emissive: 0x2266cc, emissiveIntensity: 0.42 }),
  );
  pad.position.set(0, 0.12, 0);
  pad.receiveShadow = true;
  g.add(pad);

  const beacon = new THREE.PointLight(0x66aaff, 2.8, 14);
  beacon.position.set(0.55, 3.8, 1.18);
  markAmbientPulse(beacon, { light: beacon, base: 2.25, amount: 0.32, speed: 1.15 });
  g.add(beacon);

  const collW = 4.6;
  const collH = 2.85;
  const collD = 2.35;
  const body = new CANNON.Body({ mass: 0, material: floorMaterial });
  body.addShape(new CANNON.Box(new CANNON.Vec3(collW / 2, collH / 2, collD / 2)));
  body.position.set(x, collH / 2 + 0.02, z);
  body.quaternion.setFromEuler(0, g.rotation.y, 0, 'XYZ');
  world.addBody(body);
  markDecorative(g, 145);
}

function addMannequin(root: THREE.Group, x: number, z: number, grad: THREE.DataTexture, hueShift = 0): void {
  const g = new THREE.Group();
  const bodyColor = new THREE.Color().setHSL(0.58 + hueShift, 0.58, 0.52);
  const body = new THREE.Mesh(
    new THREE.CapsuleGeometry(0.32, 1.12, 5, 10),
    toonMat(bodyColor.getHex(), grad, {
      emissive: bodyColor.clone().multiplyScalar(0.24).getHex(),
      emissiveIntensity: 0.22,
    }),
  );
  body.position.y = 1.24;
  body.castShadow = true;
  body.receiveShadow = true;
  g.add(body);
  const head = new THREE.Mesh(
    new THREE.SphereGeometry(0.24, 12, 10),
    toonMat(0xffd8b8, grad, { emissive: 0x664433, emissiveIntensity: 0.08 }),
  );
  head.position.y = 2.12;
  head.castShadow = true;
  g.add(head);
  const shoulderPad = new THREE.Mesh(
    new THREE.BoxGeometry(0.84, 0.16, 0.24),
    toonMat(0x68b7ff, grad, { emissive: 0x224466, emissiveIntensity: 0.1 }),
  );
  shoulderPad.position.y = 1.58;
  shoulderPad.castShadow = true;
  g.add(shoulderPad);
  g.position.set(x, 0, z);
  g.rotation.y = Math.atan2(-x, -z) + Math.PI * 0.08;
  root.add(g);
}

export function buildArena(
  scene: THREE.Scene,
  world: CANNON.World,
  floorMaterial: CANNON.Material,
  objectMaterial: CANNON.Material,
): BuiltWorld {
  const syncables: { mesh: THREE.Object3D; body: CANNON.Body }[] = [];
  const destructibleMeshes: THREE.Mesh[] = [];
  let buildingIndex = 0;
  const root = new THREE.Group();
  scene.add(root);
  const toonGrad = createToonGradientMap();
  const sharedMats = createSharedWorldMaterials(toonGrad);

  // === GROUND — rolling hills terrain ===
  const groundMat = new THREE.MeshToonMaterial({
    map: createFloorTexture(),
    gradientMap: toonGrad,
    color: 0x7ec850,
  });
  const { mesh: groundMesh, getHeight: getTerrainHeight } = generateTerrainMesh(groundMat);
  tagFootstepSurface(groundMesh, 'grass');
  root.add(groundMesh);

  const groundShape = new CANNON.Plane();
  const groundBody = new CANNON.Body({ mass: 0, material: floorMaterial });
  groundBody.addShape(groundShape);
  groundBody.quaternion.setFromEuler(-Math.PI / 2, 0, 0);
  world.addBody(groundBody);

  // === ARENA WALLS ===
  const wallColors = [0xf78fb3, 0x7bed9f, 0xffeaa7, 0x74b9ff];
  const wallH = 14;
  const arena = 100;
  const t = 1.2;
  const walls: { pos: [number, number, number]; size: [number, number, number]; c: number }[] = [
    { pos: [0, wallH / 2, -arena / 2], size: [arena + t * 2, wallH, t], c: wallColors[0]! },
    { pos: [0, wallH / 2, arena / 2], size: [arena + t * 2, wallH, t], c: wallColors[1]! },
    { pos: [-arena / 2, wallH / 2, 0], size: [t, wallH, arena], c: wallColors[2]! },
    { pos: [arena / 2, wallH / 2, 0], size: [t, wallH, arena], c: wallColors[3]! },
  ];
  for (const w of walls) {
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(w.size[0], w.size[1], w.size[2]),
      toonMat(w.c, toonGrad),
    );
    mesh.position.set(w.pos[0], w.pos[1], w.pos[2]);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    root.add(mesh);
    const shape = new CANNON.Box(new CANNON.Vec3(w.size[0] / 2, w.size[1] / 2, w.size[2] / 2));
    const body = new CANNON.Body({ mass: 0, material: floorMaterial });
    body.addShape(shape, new CANNON.Vec3(w.pos[0], w.pos[1], w.pos[2]));
    world.addBody(body);
  }

  // === BUILDINGS (Tilted-style POI) ===
  // Cluster of 3 buildings near "Tilted" (south)
  destructibleMeshes.push(...addBuilding(root, world, floorMaterial, 2, -38, {
    w: 8,
    d: 8,
    h: 4.4,
    wallColor: 0xeaeae6,
    roofColor: 0xa83b3b,
    grad: toonGrad,
    doorSide: 'N',
  }, `world_bld_${buildingIndex++}`));
  destructibleMeshes.push(...addBuilding(root, world, floorMaterial, -8, -38, {
    w: 6.5,
    d: 7,
    h: 3.8,
    wallColor: 0xcfe5f0,
    roofColor: 0x2e5f8a,
    grad: toonGrad,
    doorSide: 'N',
  }, `world_bld_${buildingIndex++}`));
  destructibleMeshes.push(...addBuilding(root, world, floorMaterial, 12, -36, {
    w: 7,
    d: 6,
    h: 4.0,
    wallColor: 0xf5dca7,
    roofColor: 0x7e4e1e,
    grad: toonGrad,
    doorSide: 'W',
  }, `world_bld_${buildingIndex++}`));
  addPoiSign(root, 2, -30, 'TILTED', 0xff5050, toonGrad);
  addPoiLandmarkBillboard(root, -6, -34, 'TILTED TOWERS', 0xff5050, toonGrad);
  addShieldKeg(root, SHIELD_KEG_X, SHIELD_KEG_Z, toonGrad);

  // Tilted street cover — crates + half-walls for mid-fight peeking
  addCoverCrate(root, world, floorMaterial, 7, -33, {
    w: 1.35,
    h: 1.35,
    d: 1.35,
    color: 0xc9a86c,
    grad: toonGrad,
    rotY: 0.22,
  });
  addCoverCrate(root, world, floorMaterial, 8.4, -33.8, {
    w: 1.05,
    h: 0.85,
    d: 1.05,
    color: 0x8b6914,
    grad: toonGrad,
    rotY: -0.35,
  });
  addHalfWall(root, world, floorMaterial, -3, -34, Math.PI * 0.5, {
    w: 2.8,
    h: 1.15,
    t: 0.32,
    color: 0xd4c4a8,
    grad: toonGrad,
  });

  // Retail Ridge (NW) — bigger single building
  destructibleMeshes.push(...addBuilding(root, world, floorMaterial, -35, 22, {
    w: 11,
    d: 8,
    h: 4.6,
    wallColor: 0xf2c14e,
    roofColor: 0x6a3f2c,
    grad: toonGrad,
    doorSide: 'S',
  }, `world_bld_${buildingIndex++}`));
  addPoiSign(root, -35, 16, 'RETAIL RIDGE', 0xffa64a, toonGrad);

  // Retail parking-lot cover
  addCoverCrate(root, world, floorMaterial, -31, 19, {
    w: 1.2,
    h: 1.2,
    d: 1.2,
    color: 0xe8b84a,
    grad: toonGrad,
    rotY: 0.48,
  });
  addHalfWall(root, world, floorMaterial, -38, 24, 0, {
    w: 3.2,
    h: 1.2,
    t: 0.3,
    color: 0xf0d080,
    grad: toonGrad,
  });
  addHalfWall(root, world, floorMaterial, -32, 26, Math.PI * 0.42, {
    w: 2.4,
    h: 1.05,
    t: 0.28,
    color: 0xc9a050,
    grad: toonGrad,
  });

  // Pleasant Point (NE) — coastal cabin
  destructibleMeshes.push(...addBuilding(root, world, floorMaterial, 38, 16, {
    w: 7,
    d: 7,
    h: 4.2,
    wallColor: 0xd2efff,
    roofColor: 0x1e6f8a,
    grad: toonGrad,
    doorSide: 'W',
  }, `world_bld_${buildingIndex++}`));
  addPoiSign(root, 38, 10, 'PLEASANT POINT', 0x6cd7ff, toonGrad);
  addPoiLandmarkBillboard(root, 44, 14, 'PLEASANT PARK', 0x6cd7ff, toonGrad);
  addCraftBench(root, CRAFT_BENCH_X, CRAFT_BENCH_Z, toonGrad);
  addNpcStandee(root, 37.2, 11.2, toonGrad);
  addNpcStandee(root, -17.2, -11.4, toonGrad);
  addNpcStandee(root, -19.4, -25.6, toonGrad);

  // Lucky Wharf (SW)
  destructibleMeshes.push(...addBuilding(root, world, floorMaterial, -22, -28, {
    w: 6,
    d: 6,
    h: 3.6,
    wallColor: 0xebd6a4,
    roofColor: 0x3f8243,
    grad: toonGrad,
    doorSide: 'E',
  }, `world_bld_${buildingIndex++}`));
  addPoiSign(root, -22, -22, 'LUCKY WHARF', 0xb6ff7d, toonGrad);
  const wharfDock = new THREE.Mesh(
    new THREE.PlaneGeometry(5.2, 3.6),
    toonMat(0x5a6a78, toonGrad, { emissive: 0x1a2838, emissiveIntensity: 0.22 }),
  );
  wharfDock.rotation.x = -Math.PI / 2;
  wharfDock.position.set(-25.5, 0.04, -29.5);
  wharfDock.receiveShadow = true;
  tagFootstepSurface(wharfDock, 'metal');
  root.add(wharfDock);

  // Cracked Cove (SE beach)
  destructibleMeshes.push(...addBuilding(root, world, floorMaterial, 28, -22, {
    w: 6.5,
    d: 6.5,
    h: 3.8,
    wallColor: 0xa8e8f0,
    roofColor: 0x2a6a7a,
    grad: toonGrad,
    doorSide: 'N',
  }, `world_bld_${buildingIndex++}`));
  addPoiSign(root, 28, -16, 'CRACKED COVE', 0x3ecfd4, toonGrad);
  addSlurpBarrel(root, SLURP_BARREL_X, SLURP_BARREL_Z, toonGrad);

  // Dusty Depot (sand biome)
  destructibleMeshes.push(...addBuilding(root, world, floorMaterial, 32, -32, {
    w: 9,
    d: 7,
    h: 4.0,
    wallColor: 0xd4b896,
    roofColor: 0x8b5a2b,
    grad: toonGrad,
    doorSide: 'W',
  }, `world_bld_${buildingIndex++}`));
  addPoiSign(root, 32, -26, 'DUSTY DEPOT', 0xe8a84a, toonGrad);
  const depotPad = new THREE.Mesh(
    new THREE.PlaneGeometry(4.8, 3.2),
    toonMat(0x8a9098, toonGrad, { emissive: 0x2a3038, emissiveIntensity: 0.18 }),
  );
  depotPad.rotation.x = -Math.PI / 2;
  depotPad.position.set(35.5, 0.04, -30.5);
  depotPad.receiveShadow = true;
  tagFootstepSurface(depotPad, 'metal');
  root.add(depotPad);

  // Snobby Shores (snow biome)
  destructibleMeshes.push(...addBuilding(root, world, floorMaterial, -30, 30, {
    w: 7.5,
    d: 7,
    h: 4.4,
    wallColor: 0xe8f4ff,
    roofColor: 0x5a8ab0,
    grad: toonGrad,
    doorSide: 'S',
  }, `world_bld_${buildingIndex++}`));
  addPoiSign(root, -30, 24, 'SNOBBY SHORES', 0x9ad4ff, toonGrad);

  // Shifty Shafts (mine POI) — shack, tunnel mouth, interior cover
  destructibleMeshes.push(...addBuilding(root, world, floorMaterial, -18, -12, {
    w: 5,
    d: 4.5,
    h: 3.2,
    wallColor: 0x8b7355,
    roofColor: 0x4a3728,
    grad: toonGrad,
    doorSide: 'E',
  }, `world_bld_${buildingIndex++}`));
  addPoiSign(root, -18, -6, 'SHIFTY SHAFTS', 0xc4a882, toonGrad);
  // Tunnel entrance — stacked box frame east of shack
  addCoverCrate(root, world, floorMaterial, -14.2, -13.1, {
    w: 1.1,
    h: 2.05,
    d: 1.05,
    color: 0x5c4a3a,
    grad: toonGrad,
    rotY: 0,
  });
  addCoverCrate(root, world, floorMaterial, -14.2, -10.9, {
    w: 1.1,
    h: 2.05,
    d: 1.05,
    color: 0x5c4a3a,
    grad: toonGrad,
    rotY: 0,
  });
  addCoverCrate(root, world, floorMaterial, -14.2, -12, {
    w: 1.35,
    h: 0.75,
    d: 2.6,
    color: 0x4a3c30,
    grad: toonGrad,
    rotY: 0,
  });
  addCoverCrate(root, world, floorMaterial, -13.1, -12, {
    w: 1.05,
    h: 1.15,
    d: 1.8,
    color: 0x6b5848,
    grad: toonGrad,
    rotY: 0.08,
  });
  // Interior cover crates
  addCoverCrate(root, world, floorMaterial, -17.2, -12.6, {
    w: 1.05,
    h: 0.95,
    d: 1.05,
    color: 0xa08050,
    grad: toonGrad,
    rotY: 0.35,
  });
  addCoverCrate(root, world, floorMaterial, -18.8, -11.4, {
    w: 0.95,
    h: 0.85,
    d: 0.95,
    color: 0x8b6914,
    grad: toonGrad,
    rotY: -0.22,
  });
  addCoverCrate(root, world, floorMaterial, -19.1, -12.8, {
    w: 1.15,
    h: 1.05,
    d: 1.15,
    color: 0xc9a86c,
    grad: toonGrad,
    rotY: 0.48,
  });

  // === LOOT CRATES SCATTERED ===
  const rarityHues = [0.12, 0.28, 0.55, 0.78, 0.92];
  for (let i = 0; i < 50; i++) {
    const s = 0.55 + Math.random() * 1.45;
    const isMetal = Math.random() > 0.48;
    const hue = rarityHues[Math.floor(Math.random() * rarityHues.length)]!;
    const c = new THREE.Color().setHSL(hue, isMetal ? 0.12 : 0.62, isMetal ? 0.55 : 0.52);
    const mat = isMetal
      ? new THREE.MeshStandardMaterial({
          color: c.getHex(),
          roughness: 0.32,
          metalness: 0.72,
          emissive: c.clone().multiplyScalar(0.12).getHex(),
          emissiveIntensity: 0.06,
          envMapIntensity: 1.25,
        })
      : toonMat(c.getHex(), toonGrad, {
          emissive: c.clone().multiplyScalar(0.35).getHex(),
          emissiveIntensity: 0.18,
        });
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(s, s, s), mat);
    if (!isMetal && Math.random() < 0.36) {
      const tm = mat as THREE.MeshToonMaterial;
      mesh.userData.lootGlow = { mat: tm, base: tm.emissiveIntensity };
    }
    const x = (Math.random() - 0.5) * 90;
    const z = (Math.random() - 0.5) * 90;
    const y = s / 2 + 0.05;
    mesh.position.set(x, y, z);
    mesh.rotation.set(Math.random() * 0.08, Math.random() * Math.PI * 2, Math.random() * 0.08);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    root.add(mesh);
    const shape = new CANNON.Box(new CANNON.Vec3(s / 2, s / 2, s / 2));
    const body = new CANNON.Body({
      mass: s * s * s * 32,
      material: objectMaterial,
      linearDamping: 0.06,
      angularDamping: 0.1,
    });
    body.addShape(shape);
    body.position.set(x, y, z);
    body.quaternion.set(mesh.quaternion.x, mesh.quaternion.y, mesh.quaternion.z, mesh.quaternion.w);
    world.addBody(body);
    mesh.userData.cannonBody = body;
    // Harvestable loot crates — pickaxe breaks after 3 hits via matchRules DEFAULT_HARVEST_KIND
    mesh.userData.harvestProp = 'crate';
    syncables.push({ mesh, body });
  }

  const addHarvestFence = (
    x: number,
    z: number,
    length: number,
    rotY: number,
    yOffset = 0,
  ): void => {
    const w = length;
    const h = 0.92;
    const d = 0.14;
    const mat = new THREE.MeshStandardMaterial({
      color: 0x8b6a42,
      roughness: 0.82,
      metalness: 0.04,
      emissive: 0x2a1a0a,
      emissiveIntensity: 0.06,
    });
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
    const y = h / 2 + 0.02 + yOffset;
    mesh.position.set(x, y, z);
    mesh.rotation.y = rotY;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData.harvestProp = 'fence';
    root.add(mesh);
    const shape = new CANNON.Box(new CANNON.Vec3(w / 2, h / 2, d / 2));
    const body = new CANNON.Body({
      mass: 14,
      material: objectMaterial,
      linearDamping: 0.08,
      angularDamping: 0.12,
    });
    body.addShape(shape);
    body.position.set(x, y, z);
    body.quaternion.setFromEuler(0, rotY, 0);
    world.addBody(body);
    mesh.userData.cannonBody = body;
    syncables.push({ mesh, body });
  };

  // === EXPLOSIVE BARRELS ===
  const barrelColors = [0xe74c3c, 0xe67e22, 0x9b59b6, 0x3498db];
  for (let i = 0; i < 22; i++) {
    const r = 0.42 + Math.random() * 0.28;
    const h = 1.35 + Math.random() * 0.85;
    const bc = barrelColors[i % barrelColors.length]!;
    const mesh = new THREE.Mesh(
      new THREE.CylinderGeometry(r, r * 0.92, h, 18),
      new THREE.MeshStandardMaterial({
        color: bc,
        roughness: 0.38,
        metalness: 0.35,
        emissive: bc,
        emissiveIntensity: 0.08,
        envMapIntensity: 1.05,
      }),
    );
    const x = (Math.random() - 0.5) * 86;
    const z = (Math.random() - 0.5) * 86;
    const y = h / 2 + 0.02;
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData.harvestProp = 'barrel';
    root.add(mesh);
    const shape = new CANNON.Cylinder(r * 0.92, r, h, 12);
    const body = new CANNON.Body({
      mass: 22,
      material: objectMaterial,
      linearDamping: 0.05,
      angularDamping: 0.09,
    });
    body.addShape(shape);
    body.position.set(x, y, z);
    world.addBody(body);
    mesh.userData.cannonBody = body;
    syncables.push({ mesh, body });
  }

  addHarvestFence(6, -34, 3.4, 0.22, getTerrainHeight(6, -34));
  addHarvestFence(-32, 20, 2.8, -0.45, getTerrainHeight(-32, 20));
  addHarvestFence(24, -20, 3.1, 0.78, getTerrainHeight(24, -20));
  addHarvestFence(-20, -10, 2.6, 1.12, getTerrainHeight(-20, -10));
  addHarvestFence(36, 12, 2.4, -0.62, getTerrainHeight(36, 12));
  addHarvestFence(-28, 28, 3.0, 0.35, getTerrainHeight(-28, 28));

  // === CENTER TOWER (multi-tier) ===
  const towerPalette = [0xff6b9d, 0x54e0c7, 0xffd93d, 0x6c9eff];
  const tw = 8;
  for (let layer = 0; layer < 4; layer++) {
    const inset = layer * 0.6;
    const w = tw - inset * 2;
    const h = 5.2;
    const y = h / 2 + layer * h;
    const tc = towerPalette[layer]!;
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(w, h, w),
      toonMat(tc, toonGrad, {
        emissive: new THREE.Color(tc).multiplyScalar(0.28).getHex(),
        emissiveIntensity: 0.32,
      }),
    );
    mesh.position.set(0, y, 0);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    root.add(mesh);
    const shape = new CANNON.Box(new CANNON.Vec3(w / 2, h / 2, w / 2));
    const body = new CANNON.Body({ mass: 0, material: floorMaterial });
    body.addShape(shape, new CANNON.Vec3(0, y, 0));
    world.addBody(body);

    const winMat = sharedMats.windowGlass;
    const faceCount = 8;
    for (let f = 0; f < faceCount; f++) {
      const ang = (f / faceCount) * Math.PI * 2;
      const px = Math.cos(ang) * (w / 2 + 0.04);
      const pz = Math.sin(ang) * (w / 2 + 0.04);
      for (let row = 0; row < 2; row++) {
        const wy = y + (row - 0.5) * 1.65;
        const pos = new THREE.Vector3(px, wy, pz);
        const outward = pos.clone();
        outward.y = 0;
        outward.normalize().multiplyScalar(12).add(pos);
        const win = new THREE.Mesh(new THREE.PlaneGeometry(0.75, 1.05), winMat);
        win.position.copy(pos);
        win.lookAt(outward);
        root.add(win);
      }
    }
  }

  // === RAMP & BRIDGES ===
  const rampMat = toonMat(0xc77dff, toonGrad, { emissive: 0xffc6ff, emissiveIntensity: 0.22 });
  const ramp = new THREE.Mesh(new THREE.BoxGeometry(16, 0.35, 8), rampMat);
  ramp.position.set(22, 1.2, -18);
  ramp.rotation.z = -0.28;
  ramp.castShadow = true;
  ramp.receiveShadow = true;
  root.add(ramp);
  const rampShape = new CANNON.Box(new CANNON.Vec3(8, 0.175, 4));
  const rampBody = new CANNON.Body({ mass: 0, material: floorMaterial });
  rampBody.position.set(22, 1.2, -18);
  rampBody.quaternion.setFromEuler(-0.28, 0, 0, 'XYZ');
  rampBody.addShape(rampShape);
  world.addBody(rampBody);

  // === Hill platform ===
  const hillPlatformMat = toonMat(0x6cd7ff, toonGrad, { emissive: 0x5fa8ff, emissiveIntensity: 0.16 });
  const hillPlatform = new THREE.Mesh(new THREE.CylinderGeometry(9.5, 11.5, 2.2, 18), hillPlatformMat);
  hillPlatform.position.set(-26, 1.1, -12);
  hillPlatform.castShadow = true;
  hillPlatform.receiveShadow = true;
  root.add(hillPlatform);
  const hillBody = new CANNON.Body({ mass: 0, material: floorMaterial });
  hillBody.addShape(new CANNON.Cylinder(9.5, 11.5, 2.2, 12));
  hillBody.position.set(-26, 1.1, -12);
  world.addBody(hillBody);

  // === Bridge ===
  const bridgeMat = toonMat(0xffc66b, toonGrad, { emissive: 0xffe2a7, emissiveIntensity: 0.15 });
  const bridge = new THREE.Mesh(new THREE.BoxGeometry(18, 0.4, 4.8), bridgeMat);
  bridge.position.set(6, 2.6, 20);
  bridge.rotation.z = 0.17;
  bridge.castShadow = true;
  bridge.receiveShadow = true;
  root.add(bridge);
  const bridgeBody = new CANNON.Body({ mass: 0, material: floorMaterial });
  bridgeBody.addShape(new CANNON.Box(new CANNON.Vec3(9, 0.2, 2.4)));
  bridgeBody.position.set(6, 2.6, 20);
  bridgeBody.quaternion.setFromEuler(0, 0, 0.17, 'XYZ');
  world.addBody(bridgeBody);

  // === Trees / vegetation per biome ===
  const deco = new THREE.Group();
  root.add(deco);

  const placeLandmark = (landmark: THREE.Object3D, x: number, z: number, rotY = Math.atan2(-x, -z)): void => {
    landmark.position.set(x, getTerrainHeight(x, z), z);
    landmark.rotation.y = rotY;
    deco.add(landmark);
  };

  const placePoiAccent = (accent: THREE.Object3D, x: number, z: number, rotY = Math.atan2(-x, -z)): void => {
    accent.position.set(x, getTerrainHeight(x, z), z);
    accent.rotation.y = rotY;
    markDecorative(accent, accent.userData.cullDistance ?? 125);
    deco.add(accent);
  };

  const addPath = (start: { x: number; z: number }, end: { x: number; z: number }, width: number, color: number): void => {
    const path = buildPathStrip(start, end, width, color);
    path.position.y = getTerrainHeight(path.position.x, path.position.z) + 0.045;
    path.traverse((child) => {
      if (child instanceof THREE.Mesh) tagFootstepSurface(child, 'wood');
    });
    deco.add(path);
  };

  // === POI landmark silhouettes + visual navigation paths (decorative, no physics) ===
  placeLandmark(buildNeonBillboard('TILTED', { color: 0xff3d67, gradientMap: toonGrad, cullDistance: 190 }), 9.5, -30.5);
  placeLandmark(buildWaterTower({ color: 0xffb64a, accent: 0x5e3420, gradientMap: toonGrad, cullDistance: 180 }), -43.5, 18.5);
  placeLandmark(buildRadioMast({ color: 0xdaf2ff, accent: 0x67d8ff, gradientMap: toonGrad, cullDistance: 200 }), 43.5, 22.5);
  placeLandmark(buildDockLight({ color: 0x31465a, accent: 0xfff0a0, gradientMap: toonGrad, cullDistance: 160 }), -27.5, -22.5, Math.PI * 0.35);
  placeLandmark(buildCrane({ color: 0xffc247, accent: 0x593a20, gradientMap: toonGrad, cullDistance: 185 }), 22.5, -14.5, -Math.PI * 0.2);
  placeLandmark(buildDepotSilo({ color: 0xd9bd91, accent: 0xb56a2e, gradientMap: toonGrad, cullDistance: 175 }), 39.5, -32.5);
  placeLandmark(buildLandmarkSign('SNOBBY', { color: 0x9ad4ff, accent: 0x324a64, gradientMap: toonGrad, cullDistance: 145 }), -24.5, 34.8);
  placeLandmark(buildLandmarkSign('SHIFTY', { color: 0xc4a882, accent: 0x4a3728, gradientMap: toonGrad, cullDistance: 145 }), -11.8, -7.4);

  // === Phase 5 POI ambient identity accents (decorative, no physics) ===
  placePoiAccent(buildNeonAccentStrip({ color: 0xff3d67, accent: 0xfff0fb, gradientMap: toonGrad, cullDistance: 125, length: 3.2 }), 6.5, -33.2, Math.PI * 0.18);
  placePoiAccent(buildFlagBanner({ color: 0xff3d67, accent: 0x31203a, gradientMap: toonGrad, cullDistance: 120 }), 12.2, -27.8, -Math.PI * 0.12);
  placePoiAccent(buildFlagBanner({ color: 0xffb64a, accent: 0x5e3420, gradientMap: toonGrad, cullDistance: 120 }), -40.5, 16.2, Math.PI * 0.45);
  placePoiAccent(buildBiomeAccentCluster({ color: 0xffd76a, accent: 0x8d5b32, gradientMap: toonGrad, cullDistance: 95 }), -46.2, 21.8);
  placePoiAccent(buildSignalDish({ color: 0x67d8ff, accent: 0xdaf2ff, gradientMap: toonGrad, cullDistance: 135 }), 46.7, 19.4, -Math.PI * 0.32);
  placePoiAccent(buildNeonAccentStrip({ color: 0x3ecfd4, accent: 0xffffff, gradientMap: toonGrad, cullDistance: 125, length: 2.6 }), 39.2, 25.0, Math.PI * 0.58);
  placePoiAccent(buildFlagBanner({ color: 0xfff0a0, accent: 0x31465a, gradientMap: toonGrad, cullDistance: 120 }), -30.3, -20.2, Math.PI * 0.4);
  placePoiAccent(buildBiomeAccentCluster({ color: 0xf2d28b, accent: 0x5d9ed1, gradientMap: toonGrad, cullDistance: 95 }), -24.2, -25.8);
  placePoiAccent(buildSignalDish({ color: 0xffc247, accent: 0xfff3a8, gradientMap: toonGrad, cullDistance: 130 }), 26.8, -12.1, Math.PI * 0.8);
  placePoiAccent(buildBiomeAccentCluster({ color: 0xe8a84a, accent: 0x8b5a2b, gradientMap: toonGrad, cullDistance: 95 }), 19.4, -17.8);
  placePoiAccent(buildFlagBanner({ color: 0xd9bd91, accent: 0xb56a2e, gradientMap: toonGrad, cullDistance: 120 }), 36.5, -35.1, -Math.PI * 0.65);
  placePoiAccent(buildNeonAccentStrip({ color: 0xffa23d, accent: 0xfff2b0, gradientMap: toonGrad, cullDistance: 125, length: 2.4 }), 42.0, -30.0, Math.PI * 0.1);
  placePoiAccent(buildFlagBanner({ color: 0x9ad4ff, accent: 0xffffff, gradientMap: toonGrad, cullDistance: 120 }), -27.8, 37.2, -Math.PI * 0.2);
  placePoiAccent(buildBiomeAccentCluster({ color: 0xc9d6df, accent: 0x9ad4ff, gradientMap: toonGrad, cullDistance: 95 }), -21.8, 33.0);
  placePoiAccent(buildNeonAccentStrip({ color: 0xc4a882, accent: 0xffe5a6, gradientMap: toonGrad, cullDistance: 120, length: 2.2 }), -14.5, -9.6, Math.PI * 0.72);
  placePoiAccent(buildFlagBanner({ color: 0x8b6a45, accent: 0x3b2a1f, gradientMap: toonGrad, cullDistance: 115 }), -9.2, -5.3, -Math.PI * 0.45);

  addPath({ x: 2, z: -37 }, { x: -18, z: -12 }, 2.8, 0x5b5149);
  addPath({ x: -18, z: -12 }, { x: -35, z: 22 }, 2.4, 0x6a5c4c);
  addPath({ x: 38, z: 16 }, { x: 28, z: -22 }, 2.5, 0x5f6d70);
  addPath({ x: 28, z: -22 }, { x: 32, z: -32 }, 2.1, 0x8a6842);
  addPath({ x: -35, z: 22 }, { x: -30, z: 30 }, 2.0, 0xc9d6df);

  addPathSideMarker(deco, -2.8, -28.9, getTerrainHeight(-2.8, -28.9), -0.75, sharedMats, 0xff3d67);
  addPathSideMarker(deco, -23.1, 0.7, getTerrainHeight(-23.1, 0.7), -0.42, sharedMats, 0xffb64a);
  addPathSideMarker(deco, 34.6, 5.6, getTerrainHeight(34.6, 5.6), 0.26, sharedMats, 0x67d8ff);
  addPathSideMarker(deco, 30.7, -27.3, getTerrainHeight(30.7, -27.3), -0.18, sharedMats, 0xe8a84a);

  // Seven small traversal cover clusters bridge POI gaps without adding physics blockers.
  addDecorativeCoverCluster(deco, -9, -24, getTerrainHeight(-9, -24), sharedMats, { crate: 0xa67b42, tarp: 0x4f8f50, barrel: 0x7b5c38 });
  addDecorativeCoverCluster(deco, -27, 6, getTerrainHeight(-27, 6), sharedMats, { crate: 0xc99a4a, tarp: 0xffd76a, barrel: 0x8d5b32 });
  addDecorativeCoverCluster(deco, 26, 1, getTerrainHeight(26, 1), sharedMats, { crate: 0x7da6b8, tarp: 0x3ecfd4, barrel: 0x516b78 });
  addDecorativeCoverCluster(deco, 34, -27, getTerrainHeight(34, -27), sharedMats, { crate: 0xb7905c, tarp: 0xe8a84a, barrel: 0x8b5a2b });
  addDecorativeCoverCluster(deco, -26, 26, getTerrainHeight(-26, 26), sharedMats, { crate: 0xc9d6df, tarp: 0x9ad4ff, barrel: 0x768fa3 });
  addDecorativeCoverCluster(deco, 13, -17, getTerrainHeight(13, -17), sharedMats);
  addDecorativeCoverCluster(deco, 18, -7, getTerrainHeight(18, -7), sharedMats);

  // Phase 7: small vertical sightline reads along long POI routes; decorative only, no collision balance changes.
  placePoiAccent(buildRouteSightlineScreen({ color: 0x9f7a4d, accent: 0x4f3420, gradientMap: toonGrad, cullDistance: 125 }), -4.8, -22.4, Math.PI * 0.35);
  placePoiAccent(buildRouteSightlineScreen({ color: 0xffd76a, accent: 0x5e3420, gradientMap: toonGrad, cullDistance: 125, width: 1.9 }), -29.8, 12.2, -Math.PI * 0.18);
  placePoiAccent(buildRouteSightlineScreen({ color: 0x67d8ff, accent: 0x31465a, gradientMap: toonGrad, cullDistance: 130 }), 31.4, 5.8, Math.PI * 0.42);
  placePoiAccent(buildRouteSightlineScreen({ color: 0xe8a84a, accent: 0x8b5a2b, gradientMap: toonGrad, cullDistance: 125, height: 2.45 }), 24.4, -24.6, -Math.PI * 0.28);

  addWildflowerPatch(deco, 42, 12, toonGrad, 16);
  addWildflowerPatch(deco, 34, 18, toonGrad, 12);
  addWildflowerPatch(deco, 40, 21, toonGrad, 10);
  for (let i = 0; i < 60; i++) {
    const px = (Math.random() - 0.5) * 98;
    const pz = (Math.random() - 0.5) * 98;
    if (Math.hypot(px, pz) < 14) continue;
    // Skip biome zones
    if (Math.hypot(px + 32, pz - 28) < 22) continue;
    if (Math.hypot(px - 32, pz + 32) < 20) continue;
    destructibleMeshes.push(addStylizedTree(deco, px, pz, 0.85 + Math.random() * 0.45, toonGrad, undefined, getTerrainHeight(px, pz)));
  }
  // Snowy trees in snow biome
  for (let i = 0; i < 22; i++) {
    const ang = Math.random() * Math.PI * 2;
    const r = 5 + Math.random() * 18;
    const px = -32 + Math.cos(ang) * r;
    const pz = 28 + Math.sin(ang) * r;
    if (Math.hypot(px, pz) < 14) continue;
    destructibleMeshes.push(addSnowyTree(deco, px, pz, 0.8 + Math.random() * 0.4, toonGrad, getTerrainHeight(px, pz)));
  }
  // Palm trees in beach biome
  for (let i = 0; i < 14; i++) {
    const ang = Math.random() * Math.PI * 2;
    const r = 6 + Math.random() * 14;
    const px = 32 + Math.cos(ang) * r;
    const pz = -32 + Math.sin(ang) * r;
    if (Math.hypot(px, pz) < 14) continue;
    destructibleMeshes.push(addPalmTree(deco, px, pz, 0.9 + Math.random() * 0.4, toonGrad, getTerrainHeight(px, pz)));
  }
  // Rocks
  for (let i = 0; i < 30; i++) {
    const px = (Math.random() - 0.5) * 96;
    const pz = (Math.random() - 0.5) * 96;
    if (Math.hypot(px, pz) < 10) continue;
    const scale = 0.5 + Math.random() * 1.4;
    const isSnow = Math.hypot(px + 32, pz - 28) < 22;
    addRock(deco, px, pz, scale, toonGrad, isSnow ? 0xd6e2ec : 0x9aa3ad, getTerrainHeight(px, pz));
  }

  // Lamps
  for (let i = 0; i < 52; i++) {
    const lamp = new THREE.Mesh(
      new THREE.CylinderGeometry(0.07, 0.11, 3.2, 8),
      toonMat(0x2f3d4c, toonGrad, { emissive: 0xffeecc, emissiveIntensity: 0.35 }),
    );
    const px = (Math.random() - 0.5) * 98;
    const pz = (Math.random() - 0.5) * 98;
    if (Math.hypot(px, pz) < 10) continue;
    const th = getTerrainHeight(px, pz);
    lamp.position.set(px, 1.6 + th, pz);
    lamp.castShadow = true;
    deco.add(lamp);
    // Lamp glow ball
    const bulb = new THREE.Mesh(
      new THREE.SphereGeometry(0.18, 10, 8),
      new THREE.MeshBasicMaterial({ color: 0xfff4cc, transparent: true, opacity: 0.95 }),
    );
    bulb.position.set(px, 3.3 + th, pz);
    deco.add(bulb);
  }

  // === Distant skyline buildings (no collision) ===
  for (let k = 0; k < 6; k++) {
    const bx = -35 + k * 14;
    const bz = -42;
    const fake = new THREE.Mesh(
      new THREE.BoxGeometry(10, 8, 7),
      toonMat(0xff9ff3, toonGrad, { emissive: 0xffc9f3, emissiveIntensity: 0.2 }),
    );
    fake.position.set(bx, 4, bz);
    fake.castShadow = true;
    fake.receiveShadow = true;
    root.add(fake);
  }

  // POI glow pads
  const poiPads = [
    { x: -35, z: 22, hue: 0.62 },
    { x: 38, z: 16, hue: 0.53 },
    { x: -20, z: -28, hue: 0.11 },
    { x: 2, z: -38, hue: 0.8 },
    { x: 28, z: -22, hue: 0.48 },
    { x: 32, z: -32, hue: 0.09 },
    { x: -30, z: 30, hue: 0.58 },
    { x: -18, z: -12, hue: 0.09 },
  ];
  for (const pad of poiPads) {
    const c = new THREE.Color().setHSL(pad.hue, 0.62, 0.58);
    const disc = new THREE.Mesh(
      new THREE.CylinderGeometry(3.8, 4.2, 0.28, 18),
      toonMat(c.getHex(), toonGrad, {
        emissive: c.clone().multiplyScalar(0.35).getHex(),
        emissiveIntensity: 0.2,
      }),
    );
    disc.position.set(pad.x, 0.18, pad.z);
    disc.castShadow = true;
    disc.receiveShadow = true;
    root.add(disc);
  }

  // Seasonal meteor craters near random POI offsets
  const craterSites = [
    { poiX: -35, poiZ: 22, ox: 14, oz: -11 },
    { poiX: 28, poiZ: -22, ox: -9, oz: 13 },
  ];
  for (const site of craterSites) {
    addMeteorCrater(deco, site.poiX + site.ox, site.poiZ + site.oz, toonGrad);
  }

  // Static mannequins (decoration)
  const mannequinRoot = new THREE.Group();
  root.add(mannequinRoot);
  addMannequin(mannequinRoot, -31, 20, toonGrad, 0.02);
  addMannequin(mannequinRoot, 36, 14, toonGrad, -0.03);
  addMannequin(mannequinRoot, -19, -26, toonGrad, 0.07);
  addMannequin(mannequinRoot, 5, -35, toonGrad, -0.05);

  addRebootVan(root, world, floorMaterial, REBOOT_VAN_X, REBOOT_VAN_Z, toonGrad);
  addVendingMachine(root, world, floorMaterial, VEND_X, VEND_Z, toonGrad);

  // === Surrounding ocean ===
  const waterMat = new THREE.MeshStandardMaterial({
    color: 0x18c8ff,
    metalness: 0.88,
    roughness: 0.08,
    emissive: 0x0066bb,
    emissiveIntensity: 0.34,
    transparent: true,
    opacity: 0.82,
    envMapIntensity: 1.55,
    side: THREE.DoubleSide,
  });
  waterMat.onBeforeCompile = (shader) => {
    shader.uniforms.windTime = windUniform;
    shader.vertexShader = 'uniform float windTime;\nvarying vec3 vCausticWorld;\n' + shader.vertexShader;
    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>',
      `
      #include <begin_vertex>
      float waveAngle = position.x * 0.05 + position.y * 0.05 + windTime * 2.2;
      float waveHeight = sin(waveAngle) * 0.22 + cos(position.x * 0.02 - windTime * 1.3) * 0.12;
      transformed.z += waveHeight;
      `
    );
    shader.vertexShader = shader.vertexShader.replace(
      '#include <worldpos_vertex>',
      `
      #include <worldpos_vertex>
      vCausticWorld = worldPosition.xyz;
      `
    );
    shader.fragmentShader = 'uniform float windTime;\nvarying vec3 vCausticWorld;\n' + shader.fragmentShader;
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <emissivemap_fragment>',
      `
      #include <emissivemap_fragment>
      vec2 cuv = vCausticWorld.xz * 0.055;
      float c1 = sin(cuv.x * 4.4 + windTime * 2.8) * sin(cuv.y * 3.6 - windTime * 2.2);
      float c2 = sin(cuv.x * 2.6 - windTime * 1.5 + cuv.y * 3.1) * 0.5 + 0.5;
      float caustic = pow(max(0.0, c1 * c2), 1.75) * 0.44;
      totalEmissiveRadiance += vec3(0.1, 0.36, 0.5) * caustic;
      `
    );
  };
  const water = new THREE.Mesh(new THREE.RingGeometry(94, 480, 96), waterMat);
  water.rotation.x = -Math.PI / 2;
  water.position.y = -0.05;
  water.receiveShadow = true;
  root.add(water);

  // === Clouds ===
  const cloudGroup = new THREE.Group();
  for (let i = 0; i < 28; i++) {
    const puff = new THREE.Mesh(
      new THREE.SphereGeometry(9 + Math.random() * 18, 8, 6),
      new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 1,
        metalness: 0,
        transparent: true,
        opacity: 0.36 + Math.random() * 0.2,
        emissive: 0xffffff,
        emissiveIntensity: 0.07,
        depthWrite: false,
      }),
    );
    puff.scale.set(1.75 + Math.random() * 0.4, 0.42 + Math.random() * 0.12, 1.1 + Math.random() * 0.35);
    puff.position.set((Math.random() - 0.5) * 420, 48 + Math.random() * 42, (Math.random() - 0.5) * 420);
    cloudGroup.add(puff);
  }
  scene.add(cloudGroup);

  return { syncables, sceneRoot: root, waterMaterial: waterMat, decoGroup: deco, getTerrainHeight, destructibleMeshes };
}
